import React from 'react';
import UserContext from "../userContext";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router";

import Loader from "../Layout/Components/Loader";
import '../styles/main.scss';

const DISCLAIMER =
    "Ce lien est à usage unique. Une fois votre mot de passe créé, utilisez la page de connexion pour vos prochaines visites. Ne réutilisez pas ce lien dans l'e-mail.";

export default function Creation() {
    const [password, setPassword] = React.useState('');
    const [verify, setVerify] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [checking, setChecking] = React.useState(true);
    const [pageState, setPageState] = React.useState('pending');
    const [hintEmail, setHintEmail] = React.useState('');
    const [message, setMessage] = React.useState({ type: '', text: '' });

    const userContext = React.useContext(UserContext);
    const history = useHistory();
    const params = useParams();

    const goToLogin = (query = {}) => {
        const q = new URLSearchParams(query);
        history.push(`/login?${q.toString()}`);
    };

    React.useEffect(() => {
        let cancelled = false;
        userContext.apiReducer('creationStatus', { token: params.token })
            .then((data) => {
                if (cancelled) return;
                if (data?.email) setHintEmail(data.email);
                if (data?.status === 'already_activated') {
                    setPageState('already_activated');
                } else if (data?.status !== 'pending') {
                    setPageState('invalid');
                }
            })
            .catch(() => {
                if (!cancelled) setPageState('invalid');
            })
            .finally(() => {
                if (!cancelled) setChecking(false);
            });
        return () => { cancelled = true; };
    }, [params.token, userContext]);

    const getPasswordStrength = () => {
        if (!password) return '';
        if (password.length < 6) return 'weak';
        if (password.length < 10 || !/[A-Z]/.test(password) || !/[0-9]/.test(password)) return 'medium';
        return 'strong';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });

        if (password !== verify) {
            setMessage({ type: 'error', text: 'Les mots de passe ne correspondent pas' });
            return;
        }

        if (password.length < 6) {
            setMessage({ type: 'error', text: 'Le mot de passe doit contenir au moins 6 caractères' });
            return;
        }

        setLoading(true);

        try {
            await userContext.apiReducer('creation', {
                password: password,
                token: params.token
            });

            setLoading(false);
            setMessage({ type: 'success', text: 'Vos accès ont été créés avec succès !' });
            const query = { activated: '1' };
            if (hintEmail) query.email = hintEmail;
            setTimeout(() => goToLogin(query), 1500);
        } catch (err) {
            setLoading(false);
            console.error(err);

            const status = err.response?.status;
            const code = err.response?.data?.code;
            const email = err.response?.data?.email || hintEmail;

            if (status === 409 || code === 'ALREADY_ACTIVATED' || status === 400) {
                const query = { already: '1' };
                if (email) query.email = email;
                goToLogin(query);
                return;
            }

            setMessage({ type: 'error', text: 'Une erreur est survenue. Contactez informatique@mypacome.fr' });
        }
    };

    const showForm = pageState === 'pending' && !checking;

    return (
        <div className="auth-page">
            <div className="auth-background">
                <div className="auth-orb auth-orb--1" />
                <div className="auth-orb auth-orb--2" />
                <div className="auth-orb auth-orb--3" />
            </div>

            <div className="auth-card">
                <div className="auth-logo">
                    <img
                        src="https://i.ibb.co/3CgD7G4/new-Colors-Logo-2.webp"
                        alt="MYPacôme Logo"
                    />
                    <h1 className="auth-brand">MYPacôme</h1>
                    <p className="auth-tagline">Home Manager</p>
                </div>

                <div className="auth-title">
                    <h2>Bienvenue !</h2>
                    <p>Créez votre mot de passe pour accéder à votre espace</p>
                </div>

                {checking && (
                    <div className="auth-message auth-message--info">Vérification du lien en cours...</div>
                )}

                {pageState === 'already_activated' && !checking && (
                    <>
                        <div className="auth-message auth-message--info">
                            Votre compte est déjà actif. Ce lien ne sert qu'à la première création du mot de passe.
                        </div>
                        <button
                            type="button"
                            className="auth-btn"
                            onClick={() => goToLogin({ already: '1', ...(hintEmail && { email: hintEmail }) })}
                        >
                            Aller à la connexion
                        </button>
                    </>
                )}

                {pageState === 'invalid' && !checking && (
                    <>
                        <div className="auth-message auth-message--error">
                            Ce lien n'est plus valide. Utilisez la page de connexion ou contactez informatique@mypacome.fr
                        </div>
                        <button type="button" className="auth-btn" onClick={() => goToLogin()}>
                            Aller à la connexion
                        </button>
                    </>
                )}

                {message.text && (
                    <div className={`auth-message auth-message--${message.type}`}>
                        {message.text}
                    </div>
                )}

                {showForm && (
                    <>
                        <p className="auth-disclaimer">{DISCLAIMER}</p>
                        <form className="auth-form" onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label className="form-label" htmlFor="password">
                                    Créer un mot de passe
                                </label>
                                <input
                                    type="password"
                                    id="password"
                                    className="form-input"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    autoComplete="new-password"
                                />
                                {password && (
                                    <div className="password-strength">
                                        <div className="strength-bar">
                                            <div className={`strength-fill ${getPasswordStrength()}`} />
                                        </div>
                                        <span className="strength-text">
                                            Force: {getPasswordStrength() === 'weak' ? 'Faible' :
                                                getPasswordStrength() === 'medium' ? 'Moyenne' : 'Fort'}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="form-group">
                                <label className="form-label" htmlFor="verify">
                                    Confirmer le mot de passe
                                </label>
                                <input
                                    type="password"
                                    id="verify"
                                    className="form-input"
                                    placeholder="••••••••"
                                    value={verify}
                                    onChange={(e) => setVerify(e.target.value)}
                                    required
                                    autoComplete="new-password"
                                />
                            </div>

                            <button type="submit" className="auth-btn">
                                Créer mes accès
                            </button>
                        </form>
                    </>
                )}
            </div>

            <Loader show={loading || checking} message={checking ? "Vérification..." : "Création en cours..."} />
        </div>
    );
}
